import type { ChangeEvent, FocusEvent, InputHTMLAttributes } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import {
  addMonths,
  format,
  setHours,
  setMonth as setMonthValue,
  setMinutes,
  setSeconds,
  setYear as setYearValue,
} from 'date-fns';
import type { Locale } from 'date-fns/locale';
import { enUS, he } from 'date-fns/locale';
import { PlatformLanguageCode } from '@tov-plus/domain';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock3,
  X,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { msg } from '../../localization/i18n';
import { buttonVariants } from '../TovButton/TovButton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../TovPopover/TovPopover';
import { TovButton } from '../TovButton/TovButton';

type Granularity = 'day' | 'hour' | 'minute' | 'second';

export interface DateTimePickerProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>,
    Pick<
      DayPickerProps,
      'showOutsideDays' | 'showWeekNumber' | 'weekStartsOn'
    > {
  displayFormat?: {
    hour12?: string;
    hour24?: string;
  };
  granularity?: Granularity;
  hourCycle?: 12 | 24;
  locale?: Locale;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  yearRange?: number;
}

function resolveLocale(explicitLocale?: Locale) {
  if (explicitLocale) {
    return explicitLocale;
  }

  if (typeof document !== 'undefined') {
    const lang = document.documentElement.lang.toLowerCase();

    if (lang.startsWith(PlatformLanguageCode.Hebrew)) {
      return he;
    }
  }

  return enUS;
}

function parseLocalDateTime(value: string | undefined) {
  if (!value?.trim()) {
    return undefined;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function pad(value: number) {
  return String(value).padStart(2, '0');
}

function formatLocalDateTimeValue(date: Date, granularity: Granularity) {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());

  if (granularity === 'day') {
    return `${year}-${month}-${day}`;
  }

  const hours = pad(date.getHours());

  if (granularity === 'hour') {
    return `${year}-${month}-${day}T${hours}:00`;
  }

  const minutes = pad(date.getMinutes());

  if (granularity === 'minute') {
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function buildDisplayFormat(
  granularity: Granularity,
  hourCycle: 12 | 24,
  displayFormat?: DateTimePickerProps['displayFormat'],
) {
  if (hourCycle === 12) {
    return (
      displayFormat?.hour12 ??
      (granularity === 'day'
        ? msg('components.date_time_picker.pp')
        : granularity === 'hour'
          ? msg('components.date_time_picker.pp_hh_b')
          : granularity === 'minute'
            ? msg('components.date_time_picker.pp_hh_mm_b')
            : msg('components.date_time_picker.pp_hh_mm_ss_b'))
    );
  }

  return (
    displayFormat?.hour24 ??
    (granularity === 'day'
      ? msg('components.date_time_picker.pp')
      : granularity === 'hour'
        ? msg('components.date_time_picker.pp_hh_00')
        : granularity === 'minute'
          ? msg('components.date_time_picker.pp_hh_mm')
          : msg('components.date_time_picker.pp_hh_mm_ss'))
  );
}

function getMonthOptions(locale: Locale) {
  return Array.from({ length: 12 }, (_, index) => ({
    label: format(new Date(2026, index, 1), 'MMMM', { locale }),
    value: index,
  }));
}

function getYearOptions(yearRange: number) {
  const currentYear = new Date().getFullYear();

  return Array.from({ length: yearRange * 2 + 1 }, (_, index) => {
    const value = currentYear - yearRange + index;

    return {
      label: String(value),
      value,
    };
  });
}

function getSafeDate(
  date: Date | undefined,
  fallbackDate: Date,
  granularity: Granularity,
) {
  if (date) {
    return new Date(date);
  }

  const nextDate = new Date(fallbackDate);

  if (granularity === 'day') {
    nextDate.setHours(0, 0, 0, 0);
  }

  return nextDate;
}

function TimePartInput({
  ariaLabel,
  disabled,
  max,
  min,
  onValueChange,
  step = 1,
  value,
}: {
  ariaLabel: string;
  disabled?: boolean;
  max: number;
  min: number;
  onValueChange: (value: number) => void;
  step?: number;
  value: number;
}) {
  return (
    <input
      aria-label={ariaLabel}
      className="tov:h-9 tov:w-16 tov:rounded-md tov:border tov:border-input tov:bg-background tov:px-2 tov:text-center tov:text-sm tov:shadow-xs tov:outline-none tov:transition-[color,box-shadow] tov:focus-visible:border-ring tov:focus-visible:ring-[3px] tov:focus-visible:ring-ring/50 tov:disabled:pointer-events-none tov:disabled:cursor-not-allowed tov:disabled:opacity-50"
      disabled={disabled}
      inputMode="numeric"
      max={max}
      min={min}
      onBlur={(event) => {
        const parsed = Number.parseInt(event.target.value, 10);

        if (Number.isNaN(parsed)) {
          onValueChange(value);
          return;
        }

        onValueChange(Math.min(max, Math.max(min, parsed)));
      }}
      onChange={(event) => {
        const digitsOnly = event.target.value.replace(/\D/g, '');

        if (digitsOnly.length === 0) {
          return;
        }

        const parsed = Number.parseInt(digitsOnly, 10);

        if (!Number.isNaN(parsed)) {
          onValueChange(Math.min(max, Math.max(min, parsed)));
        }
      }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
          return;
        }

        event.preventDefault();
        const delta = event.key === 'ArrowUp' ? step : -step;
        const range = max - min + 1;
        const nextValue = ((value - min + delta + range) % range) + min;
        onValueChange(nextValue);
      }}
      type="text"
      value={pad(value)}
    />
  );
}

export function DateTimePicker({
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  className,
  defaultValue,
  disabled,
  displayFormat,
  form,
  granularity = 'minute',
  hourCycle = 24,
  id,
  locale,
  name,
  onBlur,
  onChange,
  placeholder,
  required,
  showOutsideDays = true,
  showWeekNumber,
  value,
  weekStartsOn,
  yearRange = 50,
}: DateTimePickerProps) {
  const fallbackDate = useMemo(
    () =>
      parseLocalDateTime(
        typeof defaultValue === 'string' ? defaultValue : undefined,
      ) ?? new Date(),
    [defaultValue],
  );
  const resolvedLocale = useMemo(() => resolveLocale(locale), [locale]);
  const monthOptions = useMemo(
    () => getMonthOptions(resolvedLocale),
    [resolvedLocale],
  );
  const yearOptions = useMemo(() => getYearOptions(yearRange), [yearRange]);
  const stringValue = typeof value === 'string' ? value : '';
  const selectedDate = useMemo(
    () => parseLocalDateTime(stringValue),
    [stringValue],
  );
  const [month, setMonth] = useState<Date>(selectedDate ?? fallbackDate);
  const hiddenInputId = useId();

  useEffect(() => {
    if (selectedDate) {
      setMonth(selectedDate);
    }
  }, [selectedDate]);

  const emitChange = (nextDate: Date | undefined) => {
    const nextValue = nextDate
      ? formatLocalDateTimeValue(nextDate, granularity)
      : '';
    const target = {
      form: null,
      id,
      name,
      value: nextValue,
    };

    onChange?.({
      currentTarget: target,
      target,
    } as ChangeEvent<HTMLInputElement>);
  };

  const displayValue = selectedDate
    ? format(
        selectedDate,
        buildDisplayFormat(granularity, hourCycle, displayFormat),
        { locale: resolvedLocale },
      )
    : null;
  const startYear = yearOptions[0]?.value ?? new Date().getFullYear();
  const endYear =
    yearOptions[yearOptions.length - 1]?.value ?? new Date().getFullYear();
  const previousDisabled =
    month.getFullYear() < startYear ||
    (month.getFullYear() === startYear && month.getMonth() === 0);
  const nextDisabled =
    month.getFullYear() > endYear ||
    (month.getFullYear() === endYear && month.getMonth() === 11);

  const updateDate = (updater: (current: Date) => Date) => {
    const baseDate = getSafeDate(
      selectedDate,
      month ?? fallbackDate,
      granularity,
    );
    const nextDate = updater(baseDate);

    emitChange(nextDate);
    setMonth(nextDate);
  };

  const currentPeriod =
    selectedDate?.getHours() && selectedDate.getHours() >= 12
      ? msg('components.date_time_picker.components_date_time_picker_pm')
      : msg('components.date_time_picker.components_date_time_picker_am');
  const displayHour12 = selectedDate
    ? ((selectedDate.getHours() + 11) % 12) + 1
    : 12;

  return (
    <div className="tov:w-full">
      <input
        aria-hidden="true"
        className="tov:hidden"
        disabled={disabled}
        form={form}
        id={hiddenInputId}
        name={name}
        readOnly
        required={required}
        tabIndex={-1}
        value={stringValue}
      />
      <Popover>
        <PopoverTrigger asChild disabled={disabled}>
          <TovButton
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            className={cn(
              'tov-input tov:flex tov:w-full tov:items-center tov:justify-between tov:gap-3 tov:text-left',
              !displayValue && 'tov:text-muted-foreground',
              className,
            )}
            disabled={disabled}
            id={id}
            onBlur={(event) =>
              onBlur?.(event as unknown as FocusEvent<HTMLInputElement>)
            }
          >
            <span className="tov:flex tov:min-w-0 tov:items-center tov:gap-2">
              <CalendarIcon className="tov:size-4 tov:shrink-0 tov:text-muted-foreground" />
              <span className="tov:truncate">
                {displayValue ??
                  placeholder ??
                  msg(
                    'components.date_time_picker.components_date_time_picker_pick_a_date',
                  )}
              </span>
            </span>
            <Clock3 className="tov:size-4 tov:shrink-0 tov:text-muted-foreground" />
          </TovButton>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="tov:w-[min(22rem,calc(100vw-1rem))] tov:min-w-0 tov:max-w-[calc(100vw-1rem)] tov:p-0"
        >
          <div className="tov:flex tov:flex-col">
            <div className="tov:flex tov:items-center tov:justify-between tov:gap-2 tov:border-b tov:border-border tov:px-3 tov:py-3">
              <TovButton
                aria-label={msg(
                  'components.date_time_picker.components_date_time_picker_previous_month',
                )}
                className={cn(
                  buttonVariants({ size: 'icon-sm', variant: 'outline' }),
                )}
                disabled={disabled || previousDisabled}
                onClick={() => setMonth((current) => addMonths(current, -1))}
              >
                <ChevronLeft className="tov-directional-icon tov:size-4" />
              </TovButton>
              <div className="tov:flex tov:flex-1 tov:items-center tov:justify-center tov:gap-2">
                <select
                  aria-label={msg(
                    'components.date_time_picker.components_date_time_picker_month',
                  )}
                  className="tov:h-9 tov:min-w-0 tov:rounded-md tov:border tov:border-input tov:bg-background tov:px-3 tov:text-sm tov:shadow-xs tov:outline-none tov:transition-[color,box-shadow] tov:focus-visible:border-ring tov:focus-visible:ring-[3px] tov:focus-visible:ring-ring/50"
                  disabled={disabled}
                  onChange={(event) =>
                    setMonth((current) =>
                      setMonthValue(
                        current,
                        Number.parseInt(event.target.value, 10),
                      ),
                    )
                  }
                  value={month.getMonth()}
                >
                  {monthOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={msg(
                    'components.date_time_picker.components_date_time_picker_year',
                  )}
                  className="tov:h-9 tov:rounded-md tov:border tov:border-input tov:bg-background tov:px-3 tov:text-sm tov:shadow-xs tov:outline-none tov:transition-[color,box-shadow] tov:focus-visible:border-ring tov:focus-visible:ring-[3px] tov:focus-visible:ring-ring/50"
                  disabled={disabled}
                  onChange={(event) =>
                    setMonth((current) =>
                      setYearValue(
                        current,
                        Number.parseInt(event.target.value, 10),
                      ),
                    )
                  }
                  value={month.getFullYear()}
                >
                  {yearOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <TovButton
                aria-label={msg(
                  'components.date_time_picker.components_date_time_picker_next_month',
                )}
                className={cn(
                  buttonVariants({ size: 'icon-sm', variant: 'outline' }),
                )}
                disabled={disabled || nextDisabled}
                onClick={() => setMonth((current) => addMonths(current, 1))}
              >
                <ChevronRight className="tov-directional-icon tov:size-4" />
              </TovButton>
            </div>
            <DayPicker
              className="tov:p-3"
              classNames={{
                button_next: 'tov:hidden',
                button_previous: 'tov:hidden',
                month_caption: 'tov:hidden',
                day: 'tov:h-9 tov:w-9 tov:p-0 tov:text-sm',
                day_button:
                  'tov:flex tov:h-9 tov:w-9 tov:items-center tov:justify-center tov:rounded-md tov:font-normal tov:outline-none tov:transition-colors tov:hover:bg-accent tov:hover:text-accent-foreground tov:focus-visible:border-ring tov:focus-visible:ring-[3px] tov:focus-visible:ring-ring/50',
                disabled: 'tov:pointer-events-none tov:opacity-40',
                month: 'tov:space-y-3',
                month_grid: 'tov:w-full tov:border-collapse',
                months: 'tov:flex',
                outside: 'tov:text-muted-foreground tov:opacity-55',
                selected:
                  'tov:!bg-primary tov:!text-primary-foreground tov:hover:!bg-primary',
                week: 'tov:mt-1 tov:flex tov:w-full',
                weekday:
                  'tov:w-9 tov:text-center tov:text-xs tov:font-medium tov:text-muted-foreground',
                weekdays: 'tov:flex tov:w-full',
              }}
              components={{
                Chevron: ({ orientation, className: iconClassName }) =>
                  orientation === 'left' ? (
                    <ChevronLeft
                      className={cn('tov-directional-icon', iconClassName)}
                    />
                  ) : (
                    <ChevronRight
                      className={cn('tov-directional-icon', iconClassName)}
                    />
                  ),
              }}
              disabled={disabled}
              hideNavigation
              locale={resolvedLocale}
              mode="single"
              month={month}
              onMonthChange={setMonth}
              onSelect={(nextDate) => {
                if (!nextDate) {
                  return;
                }

                const baseDate = getSafeDate(
                  selectedDate,
                  month ?? fallbackDate,
                  granularity,
                );
                nextDate.setHours(
                  baseDate.getHours(),
                  baseDate.getMinutes(),
                  baseDate.getSeconds(),
                  0,
                );
                emitChange(nextDate);
                setMonth(nextDate);
              }}
              selected={selectedDate}
              showOutsideDays={showOutsideDays}
              showWeekNumber={showWeekNumber}
              weekStartsOn={weekStartsOn}
            />
            {granularity !==
            msg(
              'components.date_time_picker.components_date_time_picker_day',
            ) ? (
              <div className="tov:flex tov:flex-wrap tov:items-center tov:justify-center tov:gap-2 tov:border-t tov:border-border tov:px-3 tov:py-3">
                <Clock3 className="tov:size-4 tov:text-muted-foreground" />
                <TimePartInput
                  ariaLabel={msg('components.date_time_picker.hours')}
                  disabled={disabled}
                  max={hourCycle === 24 ? 23 : 12}
                  min={hourCycle === 24 ? 0 : 1}
                  onValueChange={(nextHours) =>
                    updateDate((current) => {
                      if (hourCycle === 24) {
                        return setHours(current, nextHours);
                      }

                      const isPm = currentPeriod === 'PM';
                      const normalizedHour = (nextHours % 12) + (isPm ? 12 : 0);

                      return setHours(current, normalizedHour % 24);
                    })
                  }
                  value={
                    hourCycle === 24
                      ? (selectedDate?.getHours() ?? 0)
                      : displayHour12
                  }
                />
                <span className="tov:text-muted-foreground">:</span>
                <TimePartInput
                  ariaLabel={msg('components.date_time_picker.minutes')}
                  disabled={disabled}
                  max={59}
                  min={0}
                  onValueChange={(nextMinutes) =>
                    updateDate((current) => setMinutes(current, nextMinutes))
                  }
                  value={selectedDate?.getMinutes() ?? 0}
                />
                {granularity ===
                msg(
                  'components.date_time_picker.components_date_time_picker_second',
                ) ? (
                  <>
                    <span className="tov:text-muted-foreground">:</span>
                    <TimePartInput
                      ariaLabel={msg('components.date_time_picker.seconds')}
                      disabled={disabled}
                      max={59}
                      min={0}
                      onValueChange={(nextSeconds) =>
                        updateDate((current) =>
                          setSeconds(current, nextSeconds),
                        )
                      }
                      value={selectedDate?.getSeconds() ?? 0}
                    />
                  </>
                ) : null}
                {hourCycle === 12 ? (
                  <select
                    aria-label={msg(
                      'components.date_time_picker.components_date_time_picker_period',
                    )}
                    className="tov:h-9 tov:rounded-md tov:border tov:border-input tov:bg-background tov:px-3 tov:text-sm tov:shadow-xs tov:outline-none tov:transition-[color,box-shadow] tov:focus-visible:border-ring tov:focus-visible:ring-[3px] tov:focus-visible:ring-ring/50"
                    disabled={disabled}
                    onChange={(event) =>
                      updateDate((current) => {
                        const hours = current.getHours();
                        const wantsPm = event.target.value === 'PM';

                        if (wantsPm && hours < 12) {
                          return setHours(current, hours + 12);
                        }

                        if (!wantsPm && hours >= 12) {
                          return setHours(current, hours - 12);
                        }

                        return current;
                      })
                    }
                    value={currentPeriod}
                  >
                    <option value="AM">
                      {msg(
                        'components.date_time_picker.components_date_time_picker_am',
                      )}
                    </option>
                    <option value="PM">
                      {msg(
                        'components.date_time_picker.components_date_time_picker_pm',
                      )}
                    </option>
                  </select>
                ) : null}
              </div>
            ) : null}
            <div className="tov:flex tov:justify-end tov:gap-2 tov:border-t tov:border-border tov:px-3 tov:py-3">
              <TovButton
                className={cn(
                  buttonVariants({ size: 'sm', variant: 'outline' }),
                )}
                disabled={disabled || !stringValue}
                onClick={() => emitChange(undefined)}
              >
                <X className="tov:size-4" />
                {msg('general.actions.clear')}
              </TovButton>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
